import React from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { Image } from '@/libraries/design/components/image/src/image';
import { wacToken } from '@/libraries/design/foundations';
import type { IconSize } from '@/libraries/design/foundations/icon';

import type { NavigationTabsAppearance, NavigationTabsLayout } from './navigation-tabs-section';

const iconDimensions: Record<IconSize, string> = {
	'x-small': '24px',
	small: '32px',
	medium: '40px',
	large: '48px',
};
const linkCss = css({
	display: 'inline-flex',
	alignItems: 'center',
	color: 'inherit',
	textDecoration: 'none',
	cursor: 'pointer',
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
});
const wideLinkCss = css({
	gap: token('space.100'),
	whiteSpace: 'nowrap',
	padding: '12px 16px',
	border: '2px solid transparent',
	borderRadius: '100px',
});
const compactLinkCss = css({
	flexDirection: 'column',
	gap: token('space.050'),
	minWidth: '140px',
	padding: '10px',
	borderRadius: '10px',
	textAlign: 'center',
});
const inactiveLinkCss = css({ backgroundColor: wacToken('color.neutral.light100') });
const activeLinkCss = css({
	backgroundColor: wacToken('color.extended.blue100'),
	fontWeight: 700,
	textDecoration: 'underline',
});
const wideActiveLinkCss = css({ border: `2px solid ${wacToken('color.primary.blue')}` });
const compactIconCss = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '10px 16px',
});
const noIconPaddingCss = css({ padding: 0 });
const iconFrameCss = css({ display: 'inline-flex', flexShrink: 0 });
const iconImageCss = css({ objectFit: 'contain' });
const stackedLinkCss = css({ whiteSpace: 'normal', overflowWrap: 'anywhere' });

export type NavigationTabsLinkProps = {
	title: string;
	href: string;
	iconUrl?: string;
	iconSize?: IconSize;
	disableIconPadding?: boolean;
	appearance: NavigationTabsAppearance;
	layout: NavigationTabsLayout;
	isActive: boolean;
	onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export const NavigationTabsLink = ({
	title,
	href,
	iconUrl,
	iconSize,
	disableIconPadding,
	appearance,
	layout,
	isActive,
	onClick,
}: NavigationTabsLinkProps) => {
	const size = iconSize ?? 'x-small';
	const icon = iconUrl && (
		<span css={iconFrameCss} style={{ width: iconDimensions[size], height: iconDimensions[size] }}>
			<Image
				css={iconImageCss}
				src={iconUrl}
				alt=""
				width={iconDimensions[size]}
				height={iconDimensions[size]}
				draggable={false}
			/>
		</span>
	);
	return (
		<a
			css={[
				linkCss,
				appearance === 'wide' ? wideLinkCss : compactLinkCss,
				layout === 'stacked' && stackedLinkCss,
				isActive ? activeLinkCss : inactiveLinkCss,
				appearance === 'wide' && isActive && wideActiveLinkCss,
			]}
			href={href}
			aria-current={isActive ? 'page' : undefined}
			onClick={onClick}
			data-product-key="uniform"
			data-event="clicked"
			data-event-component="linkButton"
			data-event-container="navigationTabs"
		>
			{appearance === 'compact' && icon ? (
				<span css={[compactIconCss, disableIconPadding && noIconPaddingCss]}>{icon}</span>
			) : (
				icon
			)}
			{title}
		</a>
	);
};
